/**
 * URL Parameter Validation Utilities for Trade Page
 *
 * Provides helpers for validating and parsing URL parameters
 * used in the trade page routing system.
 */
import { DexProtocol } from "@cede/types";
import { DEX_CONFIG_LIST } from "@cede/utils";
import { useUnifiedTradeStore } from "@/store/unifiedTradeStore";
import { UnifiedMarketType } from "@/types/trading";

// Types
type SelectedMarket = ReturnType<typeof useUnifiedTradeStore.getState>["selectedMarket"] | null;

/**
 * Regular expression to validate Ethereum-like addresses (0x + 40 hex characters)
 */
export const ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;

/**
 * Check if a string is a valid Ethereum address
 */
export const isAddress = (value: string | null): boolean => !!value && ADDRESS_REGEX.test(value);

/**
 * Validates if the current store state matches the expected spot market
 */
export const isSpotMarketCorrect = (
  state: SelectedMarket,
  exchange: string,
  tokenTo: string,
  quoteAsset: string,
): boolean => {
  return (
    state?.type === UnifiedMarketType.SPOT &&
    state.exchangeId === exchange &&
    state.baseAsset === tokenTo &&
    state.quoteAsset === quoteAsset
  );
};

/**
 * Validates if the current store state matches the expected perpetuals market
 */
export const isPerpsMarketCorrect = (
  state: SelectedMarket,
  exchange: string,
  tokenTo: string,
  quoteAsset: string,
): boolean => {
  return (
    state?.type === UnifiedMarketType.PERPS &&
    state.exchangeId === exchange &&
    state.baseAsset === tokenTo &&
    state.quoteAsset === quoteAsset
  );
};

/**
 * Validates if the current store state matches the expected DEX market
 */
export const isDexMarketCorrect = (
  state: SelectedMarket,
  dexProtocol: string,
  chainId: string,
  tokenOutAddress: string,
  quoteAddress?: string,
): boolean => {
  return (
    state?.type === UnifiedMarketType.DEX &&
    state.dexProtocol === dexProtocol &&
    state.chainId === chainId &&
    state.baseAssetAddress?.toLowerCase() === tokenOutAddress.toLowerCase() &&
    (!quoteAddress || state.quoteAssetAddress?.toLowerCase() === quoteAddress.toLowerCase())
  );
};

/**
 * Type for trade page market type
 */
export type TradePageMarketType = "spot" | "perps" | "dex";

/**
 * Validates if the current store state matches the desired market parameters
 * Unified validator that handles all market types (spot, perps, dex)
 */
export const isMarketCorrect = (
  state: SelectedMarket,
  type: TradePageMarketType,
  params: {
    exchange?: string | null;
    tokenTo?: string | null;
    quoteAsset: string;
    dexProtocol?: string | null;
    chainId?: string | null;
    tokenOutAddress?: string | null;
    tokenInAddress?: string | null;
  },
): boolean => {
  const { exchange, tokenTo, quoteAsset, dexProtocol, chainId, tokenOutAddress, tokenInAddress } = params;

  switch (type) {
    case "spot":
      return exchange && tokenTo ? isSpotMarketCorrect(state, exchange, tokenTo, quoteAsset) : false;
    case "perps":
      return exchange && tokenTo ? isPerpsMarketCorrect(state, exchange, tokenTo, quoteAsset) : false;
    case "dex": {
      const quoteAddress = tokenInAddress || tokenOutAddress;
      return dexProtocol && chainId && tokenOutAddress
        ? isDexMarketCorrect(state, dexProtocol, chainId, tokenOutAddress, quoteAddress ?? undefined)
        : false;
    }
    default:
      return false;
  }
};

/**
 * Parsed URL parameters for the Trade Page
 */
export interface TradePageParams {
  type: TradePageMarketType | null;
  exchange: string | null;
  tokenTo: string | null;
  tokenFrom: string | null;
  dexProtocol: DexProtocol | null;
  chainId: string | null;
  tokenOutAddress: string | null;
  tokenInAddress: string | null;
  tokenToSymbol: string | null;
  tokenFromSymbol: string | null;
}

/**
 * Resolves DEX configuration from exchange ID, protocol, and chain ID
 *
 * @param exchange - Exchange ID (e.g., "aerodrome-cl|base")
 * @param dexProtocol - DEX protocol (e.g., "aerodrome-cl")
 * @param chainId - Chain ID (e.g., "8453")
 * @returns DEX config object or null if not found
 */
export const resolveDexConfig = (exchange: string | null, dexProtocol: string | null, chainId: string | null) => {
  if (exchange) {
    return DEX_CONFIG_LIST.find((dex) => dex.exchangeId === exchange) || null;
  }

  if (!dexProtocol) {
    return null;
  }

  const config = DEX_CONFIG_LIST.find((dex) => dex.protocol === dexProtocol) || null;
  if (!config) {
    return null;
  }

  if (chainId && String(config.chainId) !== chainId) {
    return null;
  }

  return config;
};

/**
 * Search params interface matching Next.js URLSearchParams
 */
interface SearchParamsLike {
  get(name: string): string | null;
}

/**
 * Parses URL search parameters into structured TradePageParams
 *
 * Supported URL parameters:
 * - type: Trading type (spot | perps | dex), defaults to "spot" for backward compatibility
 * - exchange: Exchange ID (e.g., "kucoin", "hyperliquid")
 * - tokenTo: Base asset symbol (what you're buying)
 * - tokenFrom: Quote asset symbol (what you're selling/paying with), defaults to "USDT"
 * - dexProtocol: DEX protocol (e.g., "aerodrome-cl")
 * - chainId: Chain ID for DEX (e.g., "8453")
 * - tokenOutAddress: Buy token address (tokenOut)
 * - tokenInAddress: Sell token address (tokenIn)
 * - tokenToSymbol / tokenFromSymbol: Optional symbols for DEX display/chart
 *
 * Examples:
 * - /trade?type=spot&exchange=kucoin&tokenTo=KCS&tokenFrom=USDT
 * - /trade?type=perps&exchange=hyperliquid&tokenTo=BTC
 * - /trade?tokenTo=KCS&exchange=kucoin (backward compatible, defaults to spot)
 * - /trade?type=dex&dexProtocol=aerodrome-cl&tokenOutAddress=0x...&tokenInAddress=0x...
 *
 * @param searchParams - URLSearchParams-like object with a get method
 * @returns Parsed TradePageParams object
 */
export const parseTradePageParams = (searchParams: SearchParamsLike): TradePageParams => {
  const type = searchParams.get("type") as TradePageMarketType | null;
  const exchange = searchParams.get("exchange");
  const tokenTo = searchParams.get("tokenTo");
  const tokenFrom = searchParams.get("tokenFrom");
  const dexProtocol = searchParams.get("dexProtocol");
  const chainId = searchParams.get("chainId");
  const tokenOutAddressParam = searchParams.get("tokenOutAddress") || searchParams.get("tokenToAddress");
  const tokenInAddressParam = searchParams.get("tokenInAddress") || searchParams.get("tokenFromAddress");
  const tokenToSymbol = searchParams.get("tokenToSymbol");
  const tokenFromSymbol = searchParams.get("tokenFromSymbol");

  const tokenOutAddress = tokenOutAddressParam || (type === "dex" && isAddress(tokenTo) ? tokenTo : null);
  const tokenInAddress = tokenInAddressParam || (type === "dex" && isAddress(tokenFrom) ? tokenFrom : null);

  const hasDexParams = !!(dexProtocol || chainId || tokenOutAddressParam || tokenInAddressParam);
  const dexConfig = resolveDexConfig(exchange, dexProtocol, chainId);

  return {
    type: type || (hasDexParams ? "dex" : tokenTo || exchange ? "spot" : null), // Default to spot unless DEX params exist
    exchange,
    tokenTo,
    tokenFrom,
    dexProtocol: dexConfig?.protocol ?? null,
    chainId: dexConfig ? String(dexConfig.chainId) : null,
    tokenOutAddress,
    tokenInAddress,
    tokenToSymbol,
    tokenFromSymbol,
  };
};
